/**
 * Design System - Typography Tokens
 *
 * Typography system extracted from Figma design
 * Includes font families, sizes, weights, and line heights
 */

export const typography = {
  // Font Families
  fonts: {
    primary: "'Gantari', sans-serif", // Primary font
    secondary: "'Gantari', sans-serif", // Secondary font (using primary)
    tertiary: "'Gantari', sans-serif", // Tertiary font (using primary)
    fallback: "system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif",
  },

  // Font Sizes
  size: {
    xs: '12px', // Extra small
    sm: '14px', // Small
    base: '16px', // Base/default
    lg: '18px', // Large
    xl: '20px', // Extra large
    '2xl': '24px', // 2X large
    '3xl': '30px', // 3X large
    '4xl': '36px', // 4X large
    '5xl': '48px', // 5X large
    '6xl': '60px', // 6X large
  },

  // Font Weights
  weight: {
    light: 300,
    regular: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
    extrabold: 800,
  },

  // Line Heights
  lineHeight: {
    none: '1',
    tight: '1.25',
    snug: '1.375',
    base: '1.4', // Base line height for body text
    normal: '1.5',
    relaxed: '1.625',
    loose: '2',
    // Specific line heights from design
    '20': '20px',
    '21': '21px',
    '52': '52px',
    '60': '60px',
  },

  // Letter Spacing
  letterSpacing: {
    tighter: '-0.05em',
    tight: '-0.025em',
    normal: '0',
    wide: '0.025em',
    wider: '0.05em',
    widest: '0.1em',
    // Specific from design
    '-0.5': '-0.5px',
  },

  // Text Styles (Composite styles used in design)
  styles: {
    // Headings
    h1: {
      fontSize: '48px',
      fontWeight: 600,
      lineHeight: '52px',
      fontFamily: "'Gantari', sans-serif",
      letterSpacing: '-0.5px',
    },
    h2: {
      fontSize: '36px',
      fontWeight: 600,
      lineHeight: '1.2',
      fontFamily: "'Gantari', sans-serif",
    },
    h3: {
      fontSize: '32px',
      fontWeight: 600,
      lineHeight: '1.25',
      fontFamily: "'Gantari', sans-serif",
    },
    h4: {
      fontSize: '24px',
      fontWeight: 700,
      lineHeight: 'normal',
      fontFamily: "'Gantari', sans-serif",
    },
    h5: {
      fontSize: '20px',
      fontWeight: 600,
      lineHeight: 'normal',
      fontFamily: "'Gantari', sans-serif",
    },
    h6: {
      fontSize: '18px',
      fontWeight: 500,
      lineHeight: 'normal',
      fontFamily: "'Gantari', sans-serif",
    },

    // Body Text
    bodyLarge: {
      fontSize: '18px',
      fontWeight: 400,
      lineHeight: 'normal',
      fontFamily: "'Gantari', sans-serif",
    },
    body: {
      fontSize: '16px',
      fontWeight: 400,
      lineHeight: 'normal',
      fontFamily: "'Gantari', sans-serif",
    },
    bodySmall: {
      fontSize: '14px',
      fontWeight: 400,
      lineHeight: '20px',
      fontFamily: "'Gantari', sans-serif",
    },
    bodyExtraSmall: {
      fontSize: '12px',
      fontWeight: 400,
      lineHeight: 'normal',
      fontFamily: "'Gantari', sans-serif",
    },

    // Labels & UI Text
    label: {
      fontSize: '14px',
      fontWeight: 600,
      lineHeight: 'normal',
      fontFamily: "'Gantari', sans-serif",
    },
    labelSmall: {
      fontSize: '12px',
      fontWeight: 600,
      lineHeight: 'normal',
      fontFamily: "'Gantari', sans-serif",
    },
    caption: {
      fontSize: '12px',
      fontWeight: 400,
      lineHeight: 'normal',
      fontFamily: "'Gantari', sans-serif",
    },

    // Button Text
    button: {
      fontSize: '16px',
      fontWeight: 600,
      lineHeight: 'normal',
      fontFamily: "'Gantari', sans-serif",
    },
    buttonSmall: {
      fontSize: '14px',
      fontWeight: 600,
      lineHeight: '20px',
      fontFamily: "'Gantari', sans-serif",
    },

    // Link Text
    link: {
      fontSize: '16px',
      fontWeight: 700,
      lineHeight: 'normal',
      fontFamily: "'Gantari', sans-serif",
      textDecoration: 'underline',
    },
  },
} as const;

export type TypographyToken = typeof typography;

// Helper function to apply text style
export const getTextStyle = (styleName: keyof typeof typography.styles) => {
  return typography.styles[styleName];
};

// Font family helpers
export const fontPrimary = typography.fonts.primary;
export const fontSecondary = typography.fonts.secondary;
export const fontTertiary = typography.fonts.tertiary;
